/** @typedef {{
 *   id: number|string,
 *   codigo?: string,
 *   titulo?: string,
 *   descripcion?: string,
 *   num_preguntas?: number,
 *   duracion_minutos?: number,
 *   categoria?: string,
 *   dificultad?: string,
 *   preguntas?: unknown[]
 * }} ExamApi */

export const CATEGORY_OPTIONS = ['All', 'Azure', 'AWS', 'Linux', 'General'];
export const DIFFICULTY_OPTIONS = ['All', 'Associate', 'Expert'];

const CATEGORY_ALIASES = {
  azure: 'Azure',
  aws: 'AWS',
  amazon: 'AWS',
  linux: 'Linux',
  general: 'General',
};

const DIFFICULTY_ALIASES = {
  associate: 'Associate',
  asociado: 'Associate',
  expert: 'Expert',
  experto: 'Expert',
};

/**
 * @param {string} [empresaNombre]
 * @returns {'Azure'|'AWS'|'Linux'|'General'}
 */
export function inferCategoryFromEmpresa(empresaNombre) {
  const n = (empresaNombre || '').toLowerCase();
  if (n.includes('azure')) return 'Azure';
  if (n.includes('amazon') || /\baws\b/.test(n) || n.includes('aws')) return 'AWS';
  if (n.includes('linux')) return 'Linux';
  return 'General';
}

/**
 * @param {unknown} value
 * @returns {'Azure'|'AWS'|'Linux'|'General'|null}
 */
export function normalizeCategory(value) {
  if (value == null || value === '') return null;
  const key = String(value).trim().toLowerCase();
  return CATEGORY_ALIASES[key] || null;
}

/**
 * @param {unknown} value
 * @returns {'Associate'|'Expert'|null}
 */
export function normalizeDifficulty(value) {
  if (value == null || value === '') return null;
  const key = String(value).trim().toLowerCase();
  return DIFFICULTY_ALIASES[key] || null;
}

/**
 * @param {ExamApi} ex
 * @returns {'Associate'|'Expert'}
 * @deprecated Solo fallback si el examen no define dificultad en JSON/DB.
 */
export function inferDifficultyFromExam(ex) {
  const fromField = normalizeDifficulty(ex?.dificultad);
  if (fromField) return fromField;
  // Fallback neutro (ya no usamos id % 2)
  return 'Associate';
}

/**
 * @param {Array<{ empresa?: { id?: number|string, nombre?: string }, examenes?: ExamApi[] }>} grupos
 */
export function flattenExamsWithMeta(grupos) {
  const list = [];
  for (const g of grupos || []) {
    const emp = g.empresa || {};
    for (const ex of g.examenes || []) {
      const qc =
        ex.num_preguntas ??
        (Array.isArray(ex.preguntas) ? ex.preguntas.length : null) ??
        25;
      const estimated =
        ex.duracion_minutos ?? Math.max(25, Math.round(Number(qc) * 1.8));
      const category =
        normalizeCategory(ex.categoria) ||
        inferCategoryFromEmpresa(emp.nombre);
      const difficulty =
        normalizeDifficulty(ex.dificultad) || inferDifficultyFromExam(ex);
      list.push({
        ...ex,
        empresaNombre: emp.nombre,
        empresaId: emp.id,
        category,
        difficulty,
        questionCount: Number(qc) || 0,
        estimatedMinutes: Number(estimated) || 45,
      });
    }
  }
  return list;
}

/**
 * @param {ReturnType<typeof flattenExamsWithMeta>} flat
 * @param {{ search: string, category: string, difficulty: string }} filters
 */
export function filterExams(flat, { search, category, difficulty }) {
  let r = [...flat];
  const q = (search || '').trim().toLowerCase();
  if (q) {
    r = r.filter((e) =>
      [e.codigo, e.titulo, e.descripcion, e.empresaNombre]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }
  if (category && category !== 'All') {
    r = r.filter((e) => e.category === category);
  }
  if (difficulty && difficulty !== 'All') {
    r = r.filter((e) => e.difficulty === difficulty);
  }
  return r;
}
